import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { User, LoginCredentials, RegisterData, AuthState } from '../types/user';

interface AuthContextType {
    state: AuthState;
    login: (credentials: LoginCredentials) => Promise<void>;
    register: (data: RegisterData) => Promise<void>;
    logout: () => void;
    isAdmin: boolean;
    hasPermission: (requiredRole: 'admin' | 'user') => boolean;
}

const initialState: AuthState = {
    user: null,
    isLoading: true,
    error: null
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthAction =
    | { type: 'SET_USER'; payload: User }
    | { type: 'REMOVE_USER' }
    | { type: 'SET_LOADING'; payload: boolean }
    | { type: 'SET_ERROR'; payload: string };

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
    switch (action.type) {
        case 'SET_USER':
            return {
                ...state,
                user: action.payload,
                isLoading: false,
                error: null
            };
        case 'REMOVE_USER':
            return {
                ...state,
                user: null,
                isLoading: false,
                error: null
            };
        case 'SET_LOADING':
            return {
                ...state,
                isLoading: action.payload
            };
        case 'SET_ERROR':
            return {
                ...state,
                error: action.payload,
                isLoading: false
            };
        default:
            return state;
    }
};

const AuthProviderComponent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [state, dispatch] = useReducer(authReducer, initialState);

    useEffect(() => {
        // Check for stored token and validate it
        const token = localStorage.getItem('token');
        const API_URL = import.meta.env.VITE_API_URL || "http://localhost/CKCK/Backend";
        if (token) {
            // Validate token and get user data
            fetch(`${API_URL}/api/user/profile.php`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            })
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    dispatch({ type: 'SET_USER', payload: data.data });
                } else {
                    localStorage.removeItem('token');
                    dispatch({ type: 'REMOVE_USER' });
                }
            })
            .catch(() => {
                localStorage.removeItem('token');
                dispatch({ type: 'REMOVE_USER' });
            });
        } else {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    }, []);

    const login = async (credentials: LoginCredentials) => {
        dispatch({ type: 'SET_LOADING', payload: true });
        const API_URL = import.meta.env.VITE_API_URL || "http://localhost/CKCK/Backend";
        try {
            const response = await fetch(`${API_URL}/api/auth/login.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(credentials)
            });

            const data = await response.json();

            if (data.success) {
                localStorage.setItem('token', data.data.token);
                dispatch({ type: 'SET_USER', payload: data.data.user });
            } else {
                throw new Error(data.message || 'Login failed');
            }
        } catch (error) {
            dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Login failed' });
            throw error;
        }
    };

    const register = async (data: RegisterData) => {
        dispatch({ type: 'SET_LOADING', payload: true });
        const API_URL = import.meta.env.VITE_API_URL || "http://localhost/CKCK/Backend";
        try {
            console.log('Registration data:', data); // Debug log
            
            const response = await fetch(`${API_URL}/api/auth/register.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(data)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const text = await response.text();
            console.log('Raw response:', text); // Debug log

            let responseData;
            try {
                responseData = JSON.parse(text);
            } catch (e) {
                console.error('JSON parse error:', e); // Debug log
                throw new Error('Invalid response format from server');
            }

            if (responseData.success) {
                dispatch({ type: 'SET_USER', payload: responseData.data });
            } else {
                const errorMessage = responseData.errors ? responseData.errors.join(', ') : responseData.message;
                throw new Error(errorMessage || 'Registration failed');
            }
        } catch (error) {
            console.error('Registration error:', error); // Debug log
            dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Registration failed' });
            throw error;
        }
    };

    const logout = () => {
        const API_URL = import.meta.env.VITE_API_URL || "http://localhost/CKCK/Backend";
        localStorage.removeItem('token');
        dispatch({ type: 'REMOVE_USER' });
        // Optionally call logout API endpoint
        fetch(`${API_URL}/api/auth/logout.php`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        }).catch(() => {
            // Ignore errors on logout
        });
    };

    const isAdmin = state.user?.Role === 'admin';

    const hasPermission = (requiredRole: 'admin' | 'user'): boolean => {
        if (!state.user) return false;
        if (requiredRole === 'user') return true;
        return state.user.Role === requiredRole;
    };

    return (
        <AuthContext.Provider 
            value={{ 
                state, 
                login, 
                register, 
                logout, 
                isAdmin,
                hasPermission 
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

function useAuthHook() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

// Named exports for better Fast Refresh support
export const AuthProvider = AuthProviderComponent;
export const useAuth = useAuthHook;